import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.ventriqo.com';

// Organization Schema
export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Ventriqo Consulting",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo.svg`,
    "description": "Finance-as-a-Service: Daily AI-bookkeeping + Fractional CFO for VC-backed SaaS, D2C, and cross-border businesses.",
    "email": "support@ventriqo.com",
    "telephone": "+919901390180",
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": "Kudlu Gate, Electronic City",
        "addressLocality": "Bangalore",
        "addressCountry": "IN"
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "Salman Commercial Tower, Business Bay",
        "addressLocality": "Dubai",
        "addressCountry": "AE"
      }
    ],
    "sameAs": [
      "https://www.linkedin.com/in/ca-ankit-kumar-a805993a/",
      "https://x.com/Ankit_classic"
    ],
    "founder": {
      "@type": "Person",
      "name": "Ankit Kumar",
      "jobTitle": "Director & Founder"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Service Schema
export function ServiceSchema({ name, description, provider = "Ventriqo Consulting" }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": provider,
      "url": SITE_URL
    },
    "areaServed": ["IN", "US", "AE"],
    "serviceType": "Financial Services"
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// FAQ Schema
export function FAQSchema({ faqs }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Pricing Schema (Offer)
export function PricingSchema({ plans }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Ventriqo Finance-as-a-Service",
    "description": "Daily AI-bookkeeping, compliance, and fractional CFO services.",
    "brand": { "@type": "Brand", "name": "Ventriqo" },
    "offers": plans.map(plan => ({
      "@type": "Offer",
      "name": plan.name,
      "description": plan.description,
      "priceCurrency": "INR",
      "price": plan.price,
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// Article Schema (Blog posts)
export function ArticleSchema({ title, description, url, image, datePublished, author = "Ventriqo" }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": url,
    "image": image || `${SITE_URL}/og-image.png`,
    "datePublished": datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Ventriqo",
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.svg` }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// BreadcrumbList Schema
export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
